<template>
  <v-app class="auth-page">
    <v-container fluid class="auth-split pa-0">
      <v-row no-gutters class="fill-height">
        <v-col cols="12" md="8" class="auth-split__hero">
          <v-img :src="background" alt="" class="auth-split__hero-bg" cover />
          <div class="auth-split__hero-content">
            <span class="auth-split__hero-badge">
              <v-icon color="white" size="16">mdi-storefront-outline</v-icon>
              Parceiros BRPoints
            </span>
            <h2 class="auth-split__hero-title">
              Sua plataforma de fidelização e cashback
            </h2>
            <p class="auth-split__hero-lead">
              Venda mais, fidelize clientes e acompanhe tudo em um só lugar.
            </p>
            <ul class="auth-split__hero-list">
              <li>Fidelização de clientes</li>
              <li>Dinheiro caindo na hora</li>
              <li>Bônus de 10% no cashback dos clientes indicados</li>
              <li>PDV, estoque e financeiro integrados</li>
            </ul>
            <v-btn
              elevation="0"
              class="auth-split__hero-cta"
              href="https://brpoints.com.br/parceiros/"
              target="_blank"
              rel="noopener noreferrer"
            >
              Conheça nosso site
              <v-icon right size="18">mdi-arrow-right</v-icon>
            </v-btn>
          </div>
        </v-col>

        <v-col cols="12" md="4" class="auth-split__panel">
          <v-card class="auth-card" elevation="0">
            <div class="auth-card__header">
              <v-img :src="logo" alt="BRPoints" class="auth-card__logo d-md-none" contain />
              <h1 class="auth-card__title">Olá! Seja bem-vindo</h1>
              <p class="auth-card__subtitle">Acesse o sistema e aumente as suas vendas</p>
            </div>

            <v-card-text class="auth-card__body">
              <v-form ref="form" v-model="valid" @submit.prevent="verifiyDocument">
                <v-text-field
                  v-model="user.document"
                  label="CPF"
                  outlined
                  dense
                  class="br-form-field mb-2"
                  v-mask="'###.###.###-##'"
                  :rules="rules"
                  prepend-inner-icon="mdi-card-account-details-outline"
                />

                <v-text-field
                  v-model="user.password"
                  label="Senha"
                  outlined
                  dense
                  class="br-form-field"
                  :append-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
                  :rules="rules"
                  :type="showPassword ? 'text' : 'password'"
                  prepend-inner-icon="mdi-lock-outline"
                  @click:append="showPassword = !showPassword"
                />

                <v-btn
                  type="submit"
                  :disabled="!valid"
                  :dark="valid"
                  color="primary"
                  depressed
                  block
                  large
                  class="auth-card__submit"
                >
                  Entrar
                </v-btn>

                <div class="d-flex justify-end mt-2">
                  <v-btn
                    text
                    small
                    color="primary"
                    class="auth-card__forgot"
                    @click="handleForgotPassword"
                  >
                    Esqueci a senha
                  </v-btn>
                </div>

                <v-btn
                  block
                  outlined
                  color="primary"
                  class="auth-card__partner"
                  @click="createAccount"
                >
                  Quero ser parceiro
                </v-btn>
              </v-form>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-app>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { Authenticate } from "@/services/user/Authenticate";
import { VerifyDocument } from "@/services/user/VerifyDocument";
import { CredentialType } from "@/types/user/Credential";
import { TokenType } from "@/types/user/Token";
import { PartnerDetailsType } from "@/types/partner/Partner";
import {
  validateOnboardingData,
  shouldSkipOnboarding,
  clearOnboardingSkip,
} from "@/utils/onboardingChecklist";

export default defineComponent({
  data() {
    return {
      valid: false,
      user: {
        document: "",
        password: "",
      } as CredentialType,
      rules: [
        (value: string) => {
          if (value) return true;
          return "O campo é obrigatório";
        },
      ],
      showPassword: false,
      logo: require("../../assets/logo-login.jpg"),
      background: require("../../assets/background.png"),
    };
  },
  methods: {
    async verifiyDocument() {
      const verifiyDocument = new VerifyDocument();
      await verifiyDocument.execute(this.user.document).then((res) => {
        if (res.status === "RE_REGISTRATION") {
          this.$store.dispatch("setSessionInfo", {
            email: res.email,
            name: res.name,
            reRegistration: true,
          });
          this.handleForgotPassword();
          return;
        }
        this.login();
      });
    },
    async login() {
      const authenticate = new Authenticate();
      await authenticate
        .execute(this.user)
        .then(async (res) => {
          const tokens = {
            accessToken: res.accessToken,
            refreshToken: res.refreshToken,
          } as TokenType;
          const tokensString = JSON.stringify(tokens);
          localStorage.setItem("tokens", tokensString);
          await this.$store.dispatch("clearSession");
          await this.$store.dispatch("loadSession");
          const partner = this.$store.state.partnerSession;
          if (!partner) {
            throw new Error("Partner session unavailable");
          }

          if (this.redirectToOnboardingIfNeeded(partner)) {
            return;
          }

          const redirectPath = localStorage.getItem("redirectAfterLogin");
          if (redirectPath) {
            localStorage.removeItem("redirectAfterLogin");
            this.$router.push(redirectPath);
          } else {
            this.$router.push("/");
          }
        })
        .catch(() => {
          this.$store.dispatch("showSnack", {
            message: "Erro ao efetuar o login",
            color: "red",
          });
        });
    },
    handleForgotPassword() {
      this.$router.push("/forgot-password");
    },
    createAccount() {
      this.$router.push("/onboarding");
    },
    redirectToOnboardingIfNeeded(partner: PartnerDetailsType): boolean {
      const { sections, hasPending } = validateOnboardingData(partner);
      this.$store.commit("SET_ONBOARDING_PENDING", hasPending);
      if (!hasPending) {
        clearOnboardingSkip(partner.id);
        return false;
      }

      if (shouldSkipOnboarding(partner.id)) {
        return false;
      }

      if (typeof window !== "undefined" && window.sessionStorage) {
        window.sessionStorage.setItem(
          "pendingOnboardingSections",
          JSON.stringify(sections),
        );
      }

      this.$router.push({ name: "onboardingChecklist" });
      return true;
    },
  },
  mounted() {
    this.$store.dispatch("setSessionInfo", {
      email: "",
      reRegistration: false,
    });
  },
});
</script>
